import { Field, InputProps as FluentInputProps, Input } from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../Tooltip';
import './NumberInput.scss';
import { createNumberInputInputValidationFn } from './NumberInput.utils';

interface NumberInputExclusiveProps {
  customLabel?: JSX.Element;
  label?: string;
  min?: number;
  max?: number;
  value?: number | string;
  onInput?(input: number, error?: string): void;
}

type NumberInputShapedProps = Omit<
  FluentInputProps,
  'defaultValue' | 'min' | 'max' | 'onInput' | 'size' | 'value'
>;

export interface NumberInputProps extends NumberInputShapedProps, NumberInputExclusiveProps {}

export const NumberInput: React.FC<NumberInputProps> = (props) => {
  const { onInput, min, max, value, customLabel, label, ...restProps } = props;
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const validateInput = createNumberInputInputValidationFn(min, max);

  const handleInputChange = (event: React.SyntheticEvent<HTMLElement, Event>) => {
    const input = event.target as HTMLInputElement;
    const number = Number.parseInt(input.value);
    const result = validateInput(number);

    if (result === null) {
      setError(null);
      onInput?.(number);
    } else {
      const errorMsg = t(result.key, result.value, result.substitutions);
      setError(errorMsg);
      onInput?.(number, errorMsg);
    }
  };

  const inputElement = (
    <Input
      {...restProps}
      type="number"
      min={min}
      max={max}
      aria-label={t('classbook:newhomework:minutes_label', {
        defaultValue: 'Eingabe von Minuten als Zahl erforderlich',
      })}
      value={value?.toString()}
      defaultValue={value?.toString()}
      contentAfter={
        error !== null ? (
          <Tooltip trigger={<ErrorCircleRegular className="text-danger" />} content={error} />
        ) : undefined
      }
      onInput={handleInputChange}
    />
  );

  return (
    <div className="flex gap-4 items-center numberInput" data-testid="numberInput">
      {label ? (
        <Field
          label={label}
          validationState={error !== null ? 'error' : undefined}
          validationMessage={error ?? undefined}
        >
          {inputElement}
        </Field>
      ) : (
        inputElement
      )}
      {customLabel}
    </div>
  );
};
